import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import { formatAsCurrency } from "Libs/utils";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProfileOrganizations } from "Api/billingSystem/profile/getProfileOrganizations.request";
import { getProfileEstimations } from "Api/billingSystem/costs/getProfileEstimations.request";
import { getProfileProjects } from "Api/billingSystem/profile/getProfileProjects.request";
import { getOrganizationProjects } from "Api/organizations/projects/getOrganizationProjects.request";

import type { RootState } from "Store/configureStore";
import type { Organization as ClientOrganization } from "@packages/client";

// Types
export type ProfileOrganization = {
  id: string;
  label: string;
  name: string;
  // Machine name (slug) used in console routes, e.g. /:slug/create-project.
  slug: string;
  status: ClientOrganization["status"];
  projects: number;
  projectedTotal: string;
};

// Thunk
export const getProfileOrganizationsThunk = createAppAsyncThunk(
  "billingSystem/profileOrganizations/get",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      // The profile estimate is the single source of truth for per-org cost,
      // replacing the per-org quote endpoint.
      const [organizations, estimation, profileProjects] = await Promise.all([
        getProfileOrganizations(billingProfileId),
        getProfileEstimations(billingProfileId),
        getProfileProjects(billingProfileId)
      ]);

      // ---------------------------------------------------------------------
      // TODO(billing API): the organization-level estimate total
      // (`estimation.organizations[].total`) does NOT yet include the totals of
      // the projects billed under that organization — it only covers org-level
      // line items (e.g. user licenses). Each project's total is returned
      // separately in `estimation.projects[]`. Until the API rolls project
      // totals up into the organization total, we sum them on the client here
      // so each organization row shows the true total.
      //
      // Why it matters: the UI nests projects inside organizations, so an org
      // whose only org-level cost is a $10 user license but which also bills an
      // $80 project would otherwise read as $10 (with the $80 hidden one level
      // down) instead of the correct $90. Remove this client-side roll-up once
      // the API includes project totals in the organization total.
      // ---------------------------------------------------------------------
      const projectAmountById = new Map(
        estimation.projects.map(project => [
          project.project_id,
          project.total.amount
        ])
      );
      const projectIdsByOrg = new Map<string, string[]>();
      for (const project of profileProjects.items) {
        const ids = projectIdsByOrg.get(project.organization_id) ?? [];
        ids.push(project.id);
        projectIdsByOrg.set(project.organization_id, ids);
      }

      // Parallelize the per-org project count lookups for better performance.
      const organizationsWithMetadata = await Promise.all(
        organizations.items.map(async organization => {
          const organizationId = organization.id;

          let projectCount = 0;
          try {
            const projects = await getOrganizationProjects(organizationId);
            projectCount = projects.count;
          } catch (err) {
            logger(err, {
              action: "get_organization_projects",
              organizationId
            });
          }

          // Cost comes from the profile estimate, matched by organization id.
          const estimationOrganization = estimation.organizations?.find(
            estimationOrg => estimationOrg.organization_id === organizationId
          );

          // Roll the org's project totals into the org total (see TODO above).
          const projectsTotal = (
            projectIdsByOrg.get(organizationId) ?? []
          ).reduce(
            (sum, projectId) => sum + (projectAmountById.get(projectId) ?? 0),
            0
          );
          const currencyCode = estimationOrganization?.total.currency_code;
          const combinedAmount =
            (estimationOrganization?.total.amount ?? 0) + projectsTotal;
          const projectedTotal = currencyCode
            ? formatAsCurrency(combinedAmount, currencyCode)
            : (estimationOrganization?.total.formatted ?? "");

          return {
            id: organization.id,
            label: organization.label,
            name: organization.label,
            slug: organization.name,
            status: organization.status,
            projects: projectCount,
            projectedTotal
          };
        })
      );

      return {
        billingProfileId,
        organizations: organizationsWithMetadata
      };
    } catch (err) {
      logger(err, {
        action: "get_profile_organizations",
        billingProfileId
      });
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type ProfileOrganizationsState = {
  dataByProfileId: Record<
    string,
    {
      organizations: ProfileOrganization[];
      loading: boolean;
      error?: string;
    }
  >;
};

const initialState: ProfileOrganizationsState = {
  dataByProfileId: {}
};

// Slice
const profileOrganizationsSlice = createSlice({
  name: "profileOrganizations",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getProfileOrganizationsThunk.pending, (state, { meta }) => {
        const { billingProfileId } = meta.arg;
        setDeep(state, ["dataByProfileId", billingProfileId, "loading"], true);
        setDeep(
          state,
          ["dataByProfileId", billingProfileId, "error"],
          undefined
        );
      })
      .addCase(
        getProfileOrganizationsThunk.fulfilled,
        (state, { meta, payload }) => {
          const { billingProfileId } = meta.arg;
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "loading"],
            false
          );
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "organizations"],
            payload.organizations
          );
        }
      )
      .addCase(
        getProfileOrganizationsThunk.rejected,
        (state, { meta, payload }) => {
          const { billingProfileId } = meta.arg;
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "loading"],
            false
          );
          setDeep(
            state,
            ["dataByProfileId", billingProfileId, "error"],
            payload?.error
          );
        }
      );
  }
});

export default profileOrganizationsSlice.reducer;

// Selectors
const profileOrganizationsStateSelector = (state: RootState) =>
  state.profileOrganizations;

export const selectProfileOrganizations = createSelector(
  profileOrganizationsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.dataByProfileId[billingProfileId]?.organizations ?? []
);

export const selectProfileOrganizationsLoading = createSelector(
  profileOrganizationsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.dataByProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileOrganizationsLoaded = createSelector(
  profileOrganizationsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => billingProfileId in state.dataByProfileId
);

export const selectProfileOrganizationsError = createSelector(
  profileOrganizationsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.dataByProfileId[billingProfileId]?.error
);
